import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { SwUpdate } from '@angular/service-worker';
import { environment } from '@env/environment';
import {
  AtualizacaoObrigatoriaDialogComponent,
} from '@core/pwa/atualizacao-obrigatoria-dialog.component';
import { recarregarComVersaoNova } from '@core/pwa/recarregar-app';
import { versaoAtende } from '@core/pwa/versao.utils';

/**
 * Bloqueio de quem está com uma versão que o backend não aceita mais.
 *
 * <p>O aviso do {@code AtualizacaoAppService} é um convite, e está certo assim: recarregar no meio
 * de uma manifestação com anexos custa o formulário. Mas convite não serve quando o contrato com a
 * API mudou — aí cada ação vira erro, e insistir só produz dado pela metade. Este serviço é o
 * degrau de cima, acionado pelo próprio backend.</p>
 *
 * <p>Quem decide é o servidor, não a tela: é ele que sabe qual versão do SPA ainda entende suas
 * respostas. O front só compara o que recebe com a versão que carregou.</p>
 */
@Injectable({ providedIn: 'root' })
export class AtualizacaoObrigatoriaService {

  private readonly dialog = inject(MatDialog);
  private readonly updates = inject(SwUpdate);

  /** Uma tela por sessão: várias respostas chegam juntas, e o diálogo não pode empilhar. */
  private bloqueio = false;

  /**
   * Se o bloqueio já está em tela. Lido pelo aviso de atualização opcional, que se cala quando
   * este serviço assume: somar um convite a um bloqueio daria a impressão de que existe escolha.
   */
  bloqueado(): boolean {
    return this.bloqueio;
  }

  /**
   * Compara a versão em uso com o mínimo que o backend informou e bloqueia se ficou para trás.
   *
   * <p>Mínimo ausente não bloqueia: cabeçalho que não chegou (proxy que o removeu, backend antigo)
   * não é motivo para tirar ninguém do sistema.</p>
   */
  conferir(versaoMinima: string | null | undefined): void {
    if (versaoAtende(environment.version, versaoMinima)) {
      return;
    }
    this.bloquear(versaoMinima ?? '');
  }

  /**
   * Bloqueia sem comparar nada — para quando o backend recusa a requisição por versão
   * (426 Upgrade Required). Aí ele já decidiu, e comparar de novo seria discordar de quem sabe.
   */
  bloquear(versaoMinima: string): void {
    if (this.bloqueio) {
      return;
    }
    this.bloqueio = true;

    this.dialog.open(AtualizacaoObrigatoriaDialogComponent, {
      data: { versaoEmUso: environment.version, versaoMinima },
      // Sem saída: fechar o diálogo devolveria o usuário a uma tela que não funciona mais.
      disableClose: true,
      maxWidth: '95vw',
      width: '480px',
    }).afterClosed().subscribe(() => this.recarregar());
  }

  /**
   * Recarrega de verdade — ver {@link recarregarComVersaoNova}. Um reload comum é atendido pelos
   * caches e traz de volta a mesma versão que acabou de ser recusada, e o bloqueio reaparece.
   */
  private recarregar(): void {
    void recarregarComVersaoNova(this.updates);
  }
}
